import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import * as api from '@gitbook/api-client';
import type {
    NavigationScreenSiteTabSettings,
    NavigationSiteSettingsSection,
} from '@gitbook/app-navigation';
import {
    checkIsCommunityProductKey,
    checkIsFreeProductKey,
    checkIsUnrestrictedProductKey,
    isLegacySiteType,
} from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Heading,
    Page,
    PageMain,
    PagePanelsBody,
    PageWallpaper,
    Panel,
    PanelBody,
    Separator,
    Tab,
    Tabs,
} from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { SiteAdsRevenuePanel, SiteStructure } from '../../../Components';
import { NavigationRedirect } from '../../../navigation';
import { apiQuery, useSiteCapabilityContext, useSiteContext, useTrial } from '../../../state';
import darkBgGradient from '../assets/site-settings-dark.raw.svg';
import lightBgGradient from '../assets/site-settings-light.raw.svg';
import { AdsSettings } from './Ads';
import {
    CommunityPlan,
    DeleteSite,
    EditSlug,
    GitBookTracking,
    RenameSite,
    SetupCustomDomain,
    SetupCustomProxy,
    SiteAISettings,
    SitePlan,
    SiteRedirectsPanel,
    SiteSettingsHeader,
    SiteSettingsProvider,
    SiteVisibility,
    SocialPreview,
    showSettingsSection,
    siteSettingsSections,
    UnpublishSite,
} from './Settings';
import { AdaptiveContentFeature } from './Settings/SiteAdaptiveContent/AdaptiveContentFeature';

type SiteSettingsProps = {
    section: NavigationScreenSiteTabSettings['section'];
};

// Anchor section after which to insert the Customization link in the settings sidebar.
const CUSTOMIZATION_LINK_ANCHOR_SECTION: NavigationSiteSettingsSection = 'structure';

/**
 * To add a new settings sections to the site settings page:
 *   1. Add an id (e.g. general, domains) to NavigationSiteSettingsSections in packages/app-navigation/src/types.ts
 *   2. Define the new section in the useSiteSettingsSections hook
 *   3. Add a new case in the switch statement in SiteSettingsPage
 */

/**
 * Site settings page - redirect to site screen for non-admins.
 */
export function SiteSettings(props: SiteSettingsProps) {
    const { canAdminSite } = useSiteCapabilityContext();

    if (!canAdminSite) {
        return <NavigationRedirect to={{ current: { tab: undefined } }} />;
    }

    return <SiteSettingsPage section={props.section} />;
}

/**
 * Site settings page to view and manage the settings of a site.
 */
function SiteSettingsPage(props: SiteSettingsProps) {
    const { section: currentSection = 'audience' } = props;
    const { site, organizationId } = useSiteContext();
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );

    const scrollRef = React.useRef<HTMLDivElement>(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: we want to scroll to the top when the section changes
    React.useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [currentSection]);

    return (
        <Page ref={scrollRef} className="flex-1 [scrollbar-gutter:stable]">
            <PageWallpaper
                imageLight={lightBgGradient}
                imageDark={darkBgGradient}
                className="min-h-[calc(100vh-4rem)] bg-[calc(50%+12rem)_top] bg-[length:auto] bg-fixed"
            >
                <PageMain wide>
                    <div className="flex w-full gap-x-16">
                        <aside className="sticky top-2 hidden h-full w-[198px] shrink-0 align-self-start lg:block">
                            <Tabs orientation="vertical">
                                {siteSettingsSections.map((sections, index) => {
                                    const filteredSections = sections.filter(
                                        (section) =>
                                            showSettingsSection(site, organization)[section.id]
                                    );
                                    return (
                                        <React.Fragment key={`${index}--sections`}>
                                            {index !== 0 && filteredSections.length > 0 ? (
                                                <Separator className="my-1 w-full border-t" />
                                            ) : null}
                                            {filteredSections.map((section) => (
                                                <Tab
                                                    key={section.id}
                                                    leadingIcon={section.icon}
                                                    active={currentSection === section.id}
                                                    href={{
                                                        current: {
                                                            tab: {
                                                                type: 'settings',
                                                                section: section.id,
                                                            },
                                                        },
                                                    }}
                                                    label={section.title}
                                                />
                                            ))}
                                            {sections.some(
                                                (s) => s.id === CUSTOMIZATION_LINK_ANCHOR_SECTION
                                            ) ? (
                                                <Tab
                                                    key="customization-link"
                                                    leadingIcon={Icon.Customization}
                                                    trailingIcon={Icon.ArrowUpRight}
                                                    active={false}
                                                    href={{
                                                        screen: {
                                                            tab: {
                                                                type: 'customization',
                                                                tab: 'configure',
                                                            },
                                                        },
                                                    }}
                                                    label="Customization"
                                                />
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </Tabs>
                        </aside>
                        <div className="flex w-full flex-col">
                            <PagePanelsBody>
                                {(() => {
                                    switch (currentSection) {
                                        case 'general':
                                            return (
                                                <SiteSettingsGeneral organization={organization} />
                                            );
                                        case 'audience':
                                        case 'schema':
                                            return <SiteSettingsAudience />;
                                        case 'domains':
                                            return (
                                                <SiteSettingsDomains
                                                    site={site}
                                                    organization={organization}
                                                />
                                            );
                                        case 'redirects':
                                            return <SiteRedirectsPanel />;
                                        case 'ai':
                                            return <SiteAISettings />;
                                        case 'structure':
                                            return <SiteSettingsStructure />;
                                        case 'sponsorship':
                                            return (
                                                <SiteSettingsSponsorship
                                                    organization={organization}
                                                />
                                            );
                                        case 'legal':
                                            return <SiteSettingsLegal />;
                                        case 'plan':
                                            return (
                                                <SiteSettingsPlan
                                                    site={site}
                                                    organization={organization}
                                                />
                                            );
                                        default:
                                            assertNever(currentSection);
                                    }
                                })()}
                            </PagePanelsBody>
                        </div>
                    </div>
                </PageMain>
            </PageWallpaper>
        </Page>
    );
}

function SiteSettingsGeneral(props: { organization: api.Organization }) {
    const { organization } = props;
    const { site, organizationId } = useSiteContext();
    return (
        <>
            <SiteSettingsHeader
                headingId="general"
                heading="General"
                description="Manage your site's general settings such as naming and how it appears when shared."
            />
            <Panel>
                <Heading level={2} id="site-name">
                    Site name
                </Heading>
                <PanelBody>
                    <RenameSite />
                </PanelBody>
            </Panel>
            <Panel>
                <Heading level={2} id="social-preview">
                    Sharing
                </Heading>
                <PanelBody>
                    <SocialPreview />
                </PanelBody>
            </Panel>
            {checkIsUnrestrictedProductKey(organization.plan) ? (
                <SiteSettingsProvider site={site} organizationId={organizationId}>
                    <Panel>
                        <Heading level={2} id="privacy">
                            Privacy
                        </Heading>
                        <PanelBody>
                            <GitBookTracking />
                        </PanelBody>
                    </Panel>
                </SiteSettingsProvider>
            ) : null}
            <Panel kind="danger">
                <Heading level={2} id="danger">
                    Danger zone
                </Heading>
                <PanelBody>
                    <UnpublishSite />
                    <DeleteSite />
                </PanelBody>
            </Panel>
        </>
    );
}

function SiteSettingsAudience() {
    const { canAdminSite } = useSiteCapabilityContext();

    return (
        <>
            <SiteSettingsHeader
                headingId="audience"
                heading="Audience"
                description="Choose who sees your content once published."
            />
            <Panel>
                <PanelBody>
                    <SiteVisibility />
                </PanelBody>
            </Panel>
            {canAdminSite ? <AdaptiveContentFeature /> : null}
        </>
    );
}

function SiteSettingsDomains(props: { site: api.Site; organization: api.Organization }) {
    const { site, organization } = props;
    return (
        <>
            <SiteSettingsHeader
                headingId="domain"
                heading="Domain and URL"
                description="Set up a custom domain or subdirectory to match your brand and make your site
                    easier to access and share."
            />

            <Panel>
                <Heading level={2} id="site-name">
                    Custom domain
                </Heading>
                <PanelBody>
                    <SetupCustomDomain />
                </PanelBody>
                <PanelBody>
                    <SetupCustomProxy />
                </PanelBody>
            </Panel>

            {site.hostname || site.proxy ? null : (
                <Panel>
                    <Heading level={2} id="site-name">
                        Customize URL
                    </Heading>

                    <PanelBody>
                        <EditSlug site={site} organization={organization} />
                    </PanelBody>
                </Panel>
            )}
        </>
    );
}

function SiteSettingsStructure() {
    return (
        <>
            <SiteSettingsHeader
                headingId="structure"
                heading="Structure"
                description="Set the structure that shapes your site's navigation."
            />

            <SiteStructure />
        </>
    );
}

function SiteSettingsSponsorship(props: { organization: api.Organization }) {
    const { organization } = props;
    const { organizationId, site } = useSiteContext();
    const isCommunity = checkIsCommunityProductKey(organization.plan);

    return isCommunity ? (
        <>
            <SiteSettingsHeader
                headingId="sponsorship"
                heading="Ads and sponsorship"
                description="Manage your sponsored site through ethical, relevant advertisements from our
                    partners."
            />
            <SiteAdsRevenuePanel organizationId={organizationId} site={site} />
            <Panel>
                <PanelBody>
                    <AdsSettings />
                </PanelBody>
            </Panel>
        </>
    ) : null;
}

function SiteSettingsLegal() {
    return null;
}

function SiteSettingsPlan(props: { organization: api.Organization; site: api.Site }) {
    const { organization, site } = props;
    const trial = useTrial(props);
    const sitePermissions = useSiteCapabilityContext();
    const isFreeOrTrialing =
        checkIsFreeProductKey(props.organization.plan) || trial.status === 'active';

    if (
        // Organizations on old pricing don't have access to site plans
        isLegacySiteType(site.type) ||
        // Sponsored sites are tied to the community plan and cannot be changed
        site.type === api.SiteType.Sponsored
    ) {
        return <NavigationRedirect to={{ current: { tab: { type: 'settings' } } }} />;
    }

    return (
        <>
            <SiteSettingsHeader
                headingId="plan"
                heading="Plan"
                description="Decide which plan your site is on and what features you can use."
            />
            <SitePlan
                trial={trial}
                organization={organization}
                site={site}
                sitePermissions={sitePermissions}
            />
            {isFreeOrTrialing ? (
                <Panel>
                    <PanelBody>
                        <CommunityPlan />
                    </PanelBody>
                </Panel>
            ) : null}
        </>
    );
}
